import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Upload, Link, Video, CheckCircle2, Loader2, AlertCircle, RefreshCw, ImageIcon } from 'lucide-react';
import { useVimeoUpload, getVimeoInfo } from '@/hooks/useVimeo';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface VideoUploadResult {
  vimeo_id: string;
  vimeo_uri: string;
  vimeo_player_url: string;
  thumbnail_url: string | null;
  duration: string;
}

interface VideoUploadSectionProps {
  onVideoReady: (result: VideoUploadResult) => void;
  initialVimeoId?: string;
  initialPlayerUrl?: string;
  initialThumbnailUrl?: string;
  onThumbnailRefreshed?: (thumbnail_url: string | null) => void;
}

type Mode = 'upload' | 'link';

export function VideoUploadSection({ onVideoReady, initialVimeoId, initialPlayerUrl, initialThumbnailUrl, onThumbnailRefreshed }: VideoUploadSectionProps) {
  const [mode, setMode] = useState<Mode>(initialVimeoId ? 'link' : 'upload');
  const [vimeoUrl, setVimeoUrl] = useState('');
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [refreshingThumb, setRefreshingThumb] = useState(false);
  const [currentThumb, setCurrentThumb] = useState<string | null>(initialThumbnailUrl || null);
  const [linkedVideo, setLinkedVideo] = useState<{ id: string; url: string } | null>(
    initialVimeoId ? { id: initialVimeoId, url: initialPlayerUrl || '' } : null
  );

  const { upload, uploading, progress, error: uploadError } = useVimeoUpload();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Por favor selecciona un archivo de video válido');
      return;
    }

    // Max 5GB
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('El archivo no puede superar 5GB');
      return;
    }

    try {
      const result = await upload(file, file.name.replace(/\.[^.]+$/, ''));
      onVideoReady({
        vimeo_id: result.vimeo_id,
        vimeo_uri: result.vimeo_uri,
        vimeo_player_url: result.player_url,
        thumbnail_url: result.thumbnail_url,
        duration: result.duration,
      });
      toast.success('Video subido exitosamente a Vimeo');
    } catch {
      toast.error('Error al subir el video');
    }
  };

  const handleLinkSubmit = async () => {
    if (!vimeoUrl.trim()) return;

    setFetchingInfo(true);
    try {
      const info = await getVimeoInfo(vimeoUrl);
      setLinkedVideo({ id: info.vimeo_id, url: info.player_url });
      onVideoReady({
        vimeo_id: info.vimeo_id,
        vimeo_uri: info.vimeo_uri,
        vimeo_player_url: info.player_url,
        thumbnail_url: info.thumbnail_url,
        duration: info.duration,
      });
      toast.success(`Video vinculado: ${info.name}`);
    } catch (e: any) {
      toast.error(e.message || 'No se pudo obtener info del video');
    } finally {
      setFetchingInfo(false);
    }
  };

  const handleRefreshThumbnail = async () => {
    const vimeoId = linkedVideo?.id || initialVimeoId;
    if (!vimeoId) return;
    setRefreshingThumb(true);
    try {
      const info = await getVimeoInfo(`https://vimeo.com/${vimeoId}`);
      if (info.thumbnail_url) {
        setCurrentThumb(info.thumbnail_url);
        onThumbnailRefreshed?.(info.thumbnail_url);
        toast.success('Miniatura actualizada desde Vimeo');
      } else {
        toast.info('Vimeo aún no genera la miniatura. Intenta de nuevo en 1–2 minutos.');
      }
    } catch (e: any) {
      toast.error(e.message || 'No se pudo obtener la miniatura');
    } finally {
      setRefreshingThumb(false);
    }
  };

  return (
    <div className="space-y-4">
      <Label className="flex items-center gap-2">
        <Video className="h-4 w-4" />
        Video (Vimeo)
      </Label>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'upload' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('upload')}
          className="gap-2"
        >
          <Upload className="h-3.5 w-3.5" />
          Subir archivo
        </Button>
        <Button
          type="button"
          variant={mode === 'link' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('link')}
          className="gap-2"
        >
          <Link className="h-3.5 w-3.5" />
          Pegar link de Vimeo
        </Button>
      </div>

      {/* Upload Mode */}
      {mode === 'upload' && (
        <div className="space-y-3">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
              uploading ? "border-primary/50 bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            )}
          >
            {uploading ? (
              <div className="space-y-3">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Subiendo video a Vimeo...</p>
                <Progress value={progress} variant="gradient" className="h-2" />
                <p className="text-xs text-muted-foreground font-mono">{progress}%</p>
              </div>
            ) : (
              <label className="cursor-pointer space-y-2 block">
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Haz clic o arrastra un video aquí
                </p>
                <p className="text-xs text-muted-foreground">
                  MP4, MOV, AVI — máximo 5GB
                </p>
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {uploadError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {uploadError}
            </div>
          )}
        </div>
      )}

      {/* Link Mode */}
      {mode === 'link' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="https://vimeo.com/123456789"
              value={vimeoUrl}
              onChange={(e) => setVimeoUrl(e.target.value)}
              disabled={fetchingInfo}
            />
            <Button
              type="button"
              onClick={handleLinkSubmit}
              disabled={!vimeoUrl.trim() || fetchingInfo}
              size="sm"
            >
              {fetchingInfo ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Vincular'
              )}
            </Button>
          </div>

          {linkedVideo && (
            <div className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" />
              Video vinculado
              <Badge variant="outline" className="text-xs">
                ID: {linkedVideo.id}
              </Badge>
            </div>
          )}
        </div>
      )}

      {(linkedVideo?.id || initialVimeoId) && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ImageIcon className="h-4 w-4" />
              Miniatura de Vimeo
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefreshThumbnail}
              disabled={refreshingThumb}
              className="gap-2"
            >
              {refreshingThumb ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Actualizar desde Vimeo
            </Button>
          </div>
          {currentThumb ? (
            <img
              src={currentThumb}
              alt="Miniatura del video"
              className="w-full max-w-xs rounded border"
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              Sin miniatura aún. Si acabas de subir el video, Vimeo puede tardar 1–2 minutos en generarla.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
